import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Comment } from './Comments';
import { config } from '../../config';
import { authService } from '../../services/auth';
import CommentForm from './CommentForm';
import { FaReply, FaTrash, FaThumbtack, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Icon } from '../common/Icon';
import { Link } from 'react-router-dom';

interface CommentItemProps {
  comment: Comment;
  currentUser: any;
  canModerate: boolean;
  onReply: (content: string, parentCommentId?: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onPin: (commentId: string, pinned: boolean) => Promise<void>;
  targetType: 'script' | 'profile';
  targetId: string;
  depth?: number;
  onNestedReplyAdded?: () => void;
}

const CommentContainer = styled.div<{ $depth: number; $isPinned: boolean; $isDeleted: boolean }>`
  margin-bottom: 12px;
  margin-left: ${props => props.$depth * 20}px;
  padding: 12px;
  background-color: ${props => 
    props.$isPinned ? '#2a3f2a' : 
    props.$isDeleted ? '#3a2a2a' : 
    '#2a2a2a'
  };
  border-radius: 8px;
  border-left: ${props => 
    props.$isPinned ? '3px solid #4CAF50' :
    props.$isDeleted ? '3px solid #666' :
    '3px solid transparent'
  };
  position: relative;
  max-width: ${props => `calc(100% - ${props.$depth * 20}px)`};
  display: flex;
  gap: 12px;
  
  @media (max-width: 768px) {
    margin-left: ${props => props.$depth * 10}px;
    max-width: ${props => `calc(100% - ${props.$depth * 10}px)`};
    padding: 10px;
    gap: 8px;
  }
`;

const AvatarContainer = styled.div`
  flex-shrink: 0;
`;

const CommentBody = styled.div`
  flex: 1;
  min-width: 0; /* Allows content to shrink below content size */
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #444;
  object-fit: cover;
`;

const DefaultAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #4CAF50;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Username = styled.span`
  color: #4CAF50;
  font-weight: 500;
  font-size: 0.9rem;
`;

const ProfileLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AvatarLink = styled(Link)`
  display: block;
 transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Timestamp = styled.span`
  color: #999;
  font-size: 0.8rem;
`;

const PinnedBadge = styled.span`
  background-color: #4CAF50;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
`;

const CommentContent = styled.div<{ $isDeleted: boolean }>`
  color: ${props => props.$isDeleted ? '#999' : '#cccccc'};
  line-height: 1.4;
  margin-bottom: 8px;
  font-style: ${props => props.$isDeleted ? 'italic' : 'normal'};
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.9rem;
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`;

const ActionButton = styled.button<{ $variant?: 'danger' | 'primary' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${props => 
    props.$variant === 'danger' ? '#f44336' :
    props.$variant === 'primary' ? '#4CAF50' :
    '#999'
  };
  cursor: pointer;
  font-size: 0.8rem;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${props => 
      props.$variant === 'danger' ? '#ff5252' :
      props.$variant === 'primary' ? '#66bb6a' :
      '#ccc'
    };
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RepliesContainer = styled.div`
  margin-top: 16px;
`;

const RepliesToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #4CAF50;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 8px 0;
  margin-bottom: 12px;
  
  &:hover {
    color: #66bb6a;
  }
`;

const ReplyFormContainer = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #444;
`;

const LoadingReplies = styled.div`
  color: #999;
  font-style: italic;
  padding: 12px 0;
  text-align: center;
`;

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUser,
  canModerate,
  onReply,
  onDelete,
  onPin,
  targetType,
  targetId,
  depth = 0,
  onNestedReplyAdded
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState<Comment[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  const isCommentOwner = currentUser?.id === comment.user.id;
  const canDeleteComment = isCommentOwner || canModerate;
  const canPinComment = canModerate && depth === 0; // Only top-level comments can be pinned

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 24 * 7) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Load replies
  const loadReplies = useCallback(async () => {
    if (loadingReplies || replies.length > 0) return;
    
    setLoadingReplies(true);
    
    try {
      const response = await fetch(
        `${config.apiUrl}/comments/${comment.id}/replies?offset=0&limit=50`
      );
      
      if (!response.ok) {
        throw new Error('Failed to load replies');
      }
      
      const data = await response.json();
      setReplies(data.replies);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  }, [comment.id, loadingReplies, replies.length]);

  // Handle replies toggle
  const handleRepliesToggle = () => {
    if (!showReplies && replies.length === 0) {
      loadReplies();
    }
    setShowReplies(!showReplies);
  };

  // Handle reply submission
  const handleReplySubmit = async (content: string) => {
    setReplySubmitting(true);
    
    try {
      await onReply(content, comment.id);
      setShowReplyForm(false);
      
      // Always expand and reload replies to show the new one
      setShowReplies(true);
      setReplies([]);
      loadReplies();
      
      // Notify parent CommentItems that a nested reply was added
      if (onNestedReplyAdded) {
        onNestedReplyAdded();
      }
      
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setReplySubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      onDelete(comment.id);
    }
  };

  // Handle pin toggle
  const handlePinToggle = () => {
    onPin(comment.id, !comment.is_pinned);
  };

  // Get user's initials for default avatar
  const getUserInitials = (username: string) => {
    return username.substring(0, 2).toUpperCase();
  };

  // Handle when a nested reply is added to refresh this comment's replies
  const handleNestedReplyAdded = useCallback(() => {
    if (showReplies) {
      setReplies([]);
      loadReplies();
    }
    // Also notify parent about the nested reply
    if (onNestedReplyAdded) {
      onNestedReplyAdded();
    }
  }, [showReplies, loadReplies, onNestedReplyAdded]);

  return (
    <CommentContainer 
      $depth={depth} 
      $isPinned={comment.is_pinned} 
      $isDeleted={comment.is_deleted}
    >
      <AvatarContainer>
        <AvatarLink to={`/profile/${comment.user.username}`}>
          {comment.user.profile_image ? (
            <ProfileImage 
            src={`https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${comment.user.profile_image}`} 
              alt={comment.user.username}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const nextSibling = target.nextElementSibling as HTMLElement;
                if (nextSibling) {
                  nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
          
          <DefaultAvatar style={{ display: comment.user.profile_image ? 'none' : 'flex' }}>
            {getUserInitials(comment.user.username)}
          </DefaultAvatar>
        </AvatarLink>
      </AvatarContainer>

      <CommentBody>
        <CommentHeader>
          <UserInfo>
            <ProfileLink to={`/profile/${comment.user.username}`}>
              <Username>{comment.user.username}</Username>
            </ProfileLink>
            <Timestamp>{formatTimestamp(comment.created_at)}</Timestamp>
            {comment.is_pinned && <PinnedBadge>Pinned</PinnedBadge>}
          </UserInfo>
        </CommentHeader>

        <CommentContent $isDeleted={comment.is_deleted}>
          {comment.content}
        </CommentContent>

        {!comment.is_deleted && (
          <CommentActions>
            {currentUser && (
              <ActionButton 
                onClick={() => setShowReplyForm(!showReplyForm)}
                $variant="primary"
              >
                <Icon icon={FaReply} size={12} />
                Reply
              </ActionButton>
            )}
            
            {canDeleteComment && (
              <ActionButton 
                onClick={handleDelete}
                $variant="danger"
              >
                <Icon icon={FaTrash} size={12} />
                Delete
              </ActionButton>
            )}
            
            {canPinComment && (
              <ActionButton 
                onClick={handlePinToggle}
                $variant={comment.is_pinned ? 'primary' : undefined}
              >
                <Icon icon={FaThumbtack} size={12} />
                {comment.is_pinned ? 'Unpin' : 'Pin'}
              </ActionButton>
            )}
          </CommentActions>
        )}

        {/* Reply form */}
        {showReplyForm && currentUser && (
          <ReplyFormContainer>
            <CommentForm 
              onSubmit={handleReplySubmit}
              onCancel={() => setShowReplyForm(false)}
              submitting={replySubmitting}
              placeholder={`Reply to ${comment.user.username}...`}
              compact
            />
          </ReplyFormContainer>
        )}

        {/* Replies section */}
        {comment.reply_count > 0 && (
          <RepliesContainer>
            <RepliesToggle onClick={handleRepliesToggle}>
              <Icon icon={showReplies ? FaChevronDown : FaChevronRight} size={12} />
              {comment.reply_count === 1 
                ? '1 reply' 
                : `${comment.reply_count} replies`
              }
            </RepliesToggle>

            {showReplies && (
              <div>
                {loadingReplies ? (
                  <LoadingReplies>Loading replies...</LoadingReplies>
                ) : (
                  replies.map(reply => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      currentUser={currentUser}
                      canModerate={canModerate}
                      onReply={onReply}
                      onDelete={onDelete}
                      onPin={onPin}
                      targetType={targetType}
                      targetId={targetId}
                      depth={depth + 1}
                      onNestedReplyAdded={handleNestedReplyAdded}
                    />
                  ))
                )}
              </div>
            )}
          </RepliesContainer>
        )}
      </CommentBody>
    </CommentContainer>
  );
};

export default CommentItem;